/**
 * AppReadyIndicator - Panel de carga paso a paso.
 * Opción B (elegida): el panel detallado permanece ABIERTO mostrando el progreso
 * REAL de cada componente y solo anuncia "App Lista 100%" cuando TODO está cargado
 * de verdad (ningún componente forzado por el safety-timeout). Si tras un máximo
 * razonable (~60s) algún dato no llega (yfinance con límite de peticiones), esos
 * componentes se marcan en ÁMBAR como "parcial · reintentando en 2º plano" y el
 * panel se colapsa a una píldora, para no quedarse abierto indefinidamente.
 */
import { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { useLoading } from '../contexts/LoadingContext';
import LoadingQueueContext, { SECTION_CONFIG } from '../contexts/LoadingQueueContext';
import { CheckCircle, CircleNotch, Gauge, ArrowRight, WarningCircle, X } from '@phosphor-icons/react';

const COMPONENT_NAMES = {
  'markets': { name: 'Cartera de Inversión', icon: '💼' },
  'posiblesInversiones': { name: 'Posibles Inversiones', icon: '🎯' },
  'sparklines': { name: 'Mini-gráficos', icon: '📉' },
  'candlestick': { name: 'Gráfico Velas', icon: '🕯️' },
  'clock': { name: 'Reloj', icon: '🕐' },
  'sectors': { name: 'Sectores', icon: '🏭' },
};

// Grace tras el forzado del safety-timeout antes de colapsar a "parcial".
const PARTIAL_GRACE_MS = 10000;

const AppReadyIndicator = () => {
  const {
    hasStartedLoading,
    componentStates,
    registeredComponents,
  } = useLoading();

  const [dismissed, setDismissed] = useState(false);
  const [phase, setPhase] = useState('loading'); // 'loading' | 'done' | 'done-collapsed' | 'partial'
  const [priceCoverage, setPriceCoverage] = useState(null); // { live, total, errors }
  const partialTimerRef = useRef(null);

  // Cola de carga por secciones (SECTION_CONFIG) — acceso seguro (puede no existir).
  const queueCtx = useContext(LoadingQueueContext);

  // Contador "X/Y precios en vivo" emitido por MarketsList tras cada batch.
  useEffect(() => {
    const onCoverage = (e) => {
      if (e?.detail) setPriceCoverage(e.detail);
    };
    window.addEventListener('kapital:pricesCoverage', onCoverage);
    return () => window.removeEventListener('kapital:pricesCoverage', onCoverage);
  }, []);

  const componentsList = useMemo(() => {
    return registeredComponents.map(id => ({
      id,
      ...(COMPONENT_NAMES[id] || { name: id, icon: '📦' }),
      state: componentStates[id] || { loaded: false, progress: 0 },
    }));
  }, [registeredComponents, componentStates]);

  const total = componentsList.length;
  const realLoaded = componentsList.filter(c => c.state.loaded && !c.state.forced).length;
  const forcedCount = componentsList.filter(c => c.state.forced).length;
  // Progreso REAL (sin contar los forzados) — lo que ve el usuario como honesto.
  const displayProgress = total ? Math.round((realLoaded / total) * 100) : 0;
  const reallyDone = total >= 5 && realLoaded === total;

  // Nueva carga: volver a 'loading' y mostrar el panel.
  useEffect(() => {
    if (hasStartedLoading && !reallyDone && forcedCount === 0) {
      setPhase('loading');
      setDismissed(false);
    }
  }, [hasStartedLoading, reallyDone, forcedCount]);

  // Todo cargado DE VERDAD → estado final "listo". El panel detallado permanece
  // hasta ESTE punto (100% real); el mensaje de éxito se colapsa a píldora tras 9s.
  useEffect(() => {
    if (reallyDone) {
      setPhase('done');
      window.dispatchEvent(new Event('kapital:initialLoaderDone'));
      const t = setTimeout(() => setPhase('done-collapsed'), 9000);
      return () => clearTimeout(t);
    }
  }, [reallyDone]);

  // Si el safety-timeout forzó componentes y NO se completó de verdad, dar un
  // margen mostrando "reintentando" y luego colapsar a la píldora "parcial".
  useEffect(() => {
    if (!reallyDone && forcedCount > 0 && phase === 'loading') {
      partialTimerRef.current = setTimeout(() => {
        setPhase('partial');
        window.dispatchEvent(new Event('kapital:initialLoaderDone'));
      }, PARTIAL_GRACE_MS);
      return () => clearTimeout(partialTimerRef.current);
    }
  }, [reallyDone, forcedCount, phase]);

  if (dismissed || !hasStartedLoading) return null;

  const getProgressColor = () => (reallyDone ? '#22c55e' : '#f59e0b');

  // ── Píldora final "App lista" (100% real) ─────────────────────────────
  if (phase === 'done-collapsed') {
    return (
      <div
        className="fixed bottom-4 right-4 z-[100] flex items-center gap-2 px-3 py-2 rounded-full text-xs cursor-pointer transition-all bg-green-500/20 border border-green-500/50 text-green-400 hover:bg-green-500/30"
        onClick={() => setDismissed(true)}
        data-testid="app-ready-indicator"
      >
        <CheckCircle size={16} weight="fill" />
        <span>App lista</span>
      </div>
    );
  }

  // ── Píldora "parcial" (algo sigue llegando en 2º plano) ───────────────
  if (phase === 'partial') {
    return (
      <div
        className="fixed bottom-4 right-4 z-[100] flex items-center gap-2 px-3 py-2 rounded-full text-xs cursor-pointer transition-all bg-gold-500/20 border border-gold-500/50 text-gold-300 hover:bg-gold-500/30"
        onClick={() => setDismissed(true)}
        data-testid="app-ready-indicator"
        title="Algunos datos siguen completándose en segundo plano (yfinance con límite de peticiones)"
      >
        <WarningCircle size={16} weight="fill" />
        <span>Listo · {forcedCount} en 2º plano</span>
      </div>
    );
  }

  // ── Mensaje grande de éxito "App Lista 100%" ──────────────────────────
  if (phase === 'done') {
    return (
      <div className="fixed bottom-4 right-4 z-[100] w-80 cursor-pointer" onClick={() => setDismissed(true)} data-testid="app-ready-message">
        <div
          className="bg-gradient-to-r from-green-900/90 to-emerald-900/90 border border-green-500/50 rounded-2xl p-5 shadow-2xl backdrop-blur-sm"
          style={{ boxShadow: '0 0 40px rgba(34, 197, 94, 0.4)', animation: 'readyPulse 2s ease-in-out infinite' }}
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-green-500/30 flex items-center justify-center" style={{ boxShadow: '0 4px 20px rgba(34, 197, 94, 0.3)' }}>
              <CheckCircle size={36} weight="fill" className="text-green-400" />
            </div>
            <div className="flex-1">
              <div className="text-green-300 font-bold text-lg flex items-center gap-2">
                App Lista <span className="text-green-400 animate-pulse">100%</span>
              </div>
              <div className="text-green-500/80 text-sm">{total} componentes cargados</div>
              <div className="text-green-600/70 text-xs mt-1 flex items-center gap-1">
                <ArrowRight size={10} /> Clic para cerrar
              </div>
            </div>
          </div>
        </div>
        <style>{`@keyframes readyPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.02); } }`}</style>
      </div>
    );
  }

  // ── Panel detallado paso a paso (permanece abierto hasta el 100% real) ─
  return (
    <div className="fixed bottom-4 right-4 z-[100] w-72" data-testid="app-loading-indicator">
      <div
        className="bg-zinc-900/95 border border-zinc-700 rounded-2xl p-4 shadow-2xl backdrop-blur-sm relative"
        style={{ boxShadow: `0 0 30px ${getProgressColor()}30`, transform: 'perspective(500px) rotateX(2deg)' }}
      >
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-2 right-2 text-zinc-600 hover:text-zinc-300 transition-colors"
          data-testid="app-loading-dismiss"
          title="Ocultar (la carga continúa en segundo plano)"
        >
          <X size={14} weight="bold" />
        </button>

        {/* Header */}
        <div className="flex items-center justify-between mb-3 pr-5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${getProgressColor()}20` }}>
              <Gauge size={18} style={{ color: getProgressColor() }} weight="bold" />
            </div>
            <div>
              <div className="text-white font-bold text-sm">Cargando App</div>
              <div className="text-zinc-500 text-[10px]">{realLoaded}/{total} componentes listos</div>
              {priceCoverage && Number.isFinite(priceCoverage.total) && priceCoverage.total > 0 && (
                <div className="text-[10px] mt-0.5" data-testid="app-loading-live-prices">
                  <span className={priceCoverage.live >= priceCoverage.total ? 'text-green-400' : 'text-gold-400'}>
                    {priceCoverage.live}/{priceCoverage.total} precios en vivo
                  </span>
                  {Number.isFinite(priceCoverage.errors) && priceCoverage.errors > 0 && (
                    <span className="text-red-400"> · {priceCoverage.errors} err ciclo</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="text-2xl font-bold" style={{ color: getProgressColor() }}>{displayProgress}%</div>
        </div>

        {/* Barra de progreso REAL */}
        <div className="relative h-6 bg-zinc-800 rounded-xl overflow-hidden mb-3">
          <div
            className="absolute inset-y-0 left-0 rounded-xl transition-all duration-500 ease-out"
            style={{
              width: `${displayProgress}%`,
              background: `linear-gradient(90deg, ${getProgressColor()}80, ${getProgressColor()})`,
              boxShadow: `0 0 20px ${getProgressColor()}60`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent"></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-white text-xs font-bold drop-shadow-lg">{displayProgress}% cargado</span>
          </div>
        </div>

        {/* Estado paso a paso de cada componente */}
        <div className="space-y-1.5 max-h-44 overflow-y-auto">
          {componentsList.map(component => {
            const { loaded, progress = 0, forced } = component.state;
            const isReal = loaded && !forced;
            let cls, Icon, iconCls, tail;
            if (isReal) {
              cls = 'bg-green-500/10 text-green-400';
              Icon = CheckCircle; iconCls = 'text-green-400'; tail = <span className="text-[9px] text-green-500">OK</span>;
            } else if (forced) {
              cls = 'bg-gold-500/10 text-gold-300';
              Icon = WarningCircle; iconCls = 'text-gold-400'; tail = <span className="text-[9px] text-gold-400">reintentando…</span>;
            } else if (progress > 0) {
              cls = 'bg-gold-500/10 text-gold-400';
              Icon = null; iconCls = ''; tail = <span className="text-[9px] text-zinc-500">{progress}%</span>;
            } else {
              cls = 'text-zinc-600';
              Icon = null; iconCls = ''; tail = <span className="text-[9px] text-zinc-600">en cola</span>;
            }
            return (
              <div key={component.id} className={`flex items-center gap-2 text-[10px] px-2 py-1 rounded-lg transition-all ${cls}`} data-testid={`app-loading-step-${component.id}`}>
                {Icon ? (
                  <Icon size={12} weight="fill" className={iconCls} />
                ) : progress > 0 ? (
                  <CircleNotch size={12} className="animate-spin text-gold-400" />
                ) : (
                  <div className="w-3 h-3 rounded-full border border-zinc-600"></div>
                )}
                <span className="flex-1">{component.icon} {component.name}</span>
                {tail}
              </div>
            );
          })}
        </div>

        {forcedCount > 0 && (
          <div className="mt-2 text-[9px] text-gold-400/80 leading-snug">
            Algún dato tarda (yfinance con límite de peticiones). Seguimos reintentando en 2º plano.
          </div>
        )}

        {/* Secciones del dashboard (SECTION_CONFIG) — detalle de todo lo que carga */}
        {queueCtx && typeof queueCtx.getSectionStatus === 'function' && (
          <div className="mt-2 pt-2 border-t border-zinc-800" data-testid="app-loading-sections">
            <div className="text-[9px] uppercase tracking-wider text-zinc-600 mb-1">Secciones</div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              {Object.values(SECTION_CONFIG).map(sec => {
                const st = queueCtx.getSectionStatus(sec.id); // complete | loading | pending | waiting
                const color = st === 'complete' ? 'text-green-400' : st === 'loading' ? 'text-gold-400' : 'text-zinc-600';
                const dot = st === 'complete' ? '✓' : st === 'loading' ? '…' : '·';
                return (
                  <div key={sec.id} className={`flex items-center gap-1 text-[9px] ${color}`} data-testid={`app-loading-section-${sec.id}`}>
                    <span>{dot}</span>
                    <span className="truncate">{sec.icon} {sec.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppReadyIndicator;
